import React, { useCallback } from 'react';

import { CloudProvider } from '@ariksa/abac/api';
import { IdentitySource, JitRole } from '@ariksa/jit';
import { QueryError } from 'services/types';

import { AtIcon, AwsIcon, OktaIcon } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';

export const roleNameOption = (request: JitRole, cloudType: string) => {
  switch (cloudType) {
    case 'AWS':
      return {
        label: `${request.role}`,
        value: request.role_arn,
      };
    case 'GCP':
      return {
        label: `${request.role} (${request.role_id
          ?.split('/')
          .slice(-2)
          .join('/')})`,
        value: request.role_id,
      };
    default:
      return null;
  }
};

export const jitViewByOptions = [
  { label: 'For Cloud', value: 'cloud' },
  { label: 'For Data', value: 'data' },
];

export const jitDirectoryOptions = (provider: string) => [];

export const getJITDirectoryDescription = type => {
  switch (type) {
    case 'manual':
      return 'Manually entered email ID will be used to route approval requests but cannot be verified';
    default:
      return 'We will use email IDs of directory users to route requests for approvals';
  }
};

// The directory JIT records for approvers added from this picker. Anyone
// already on the list keeps the value saved with them.
export const getJITIdentitySource = (type): IdentitySource => {
  switch (type) {
    case 'ariksa':
      return IdentitySource.Ariksa;
    case 'okta':
      return IdentitySource.Okta;
    default:
      return IdentitySource.Email;
  }
};

// Shown in place of a settings form whose saved values JIT would not return.
export const getJITLoadErrorMessage = (
  error: QueryError,
  dataGroupId?: string,
) => {
  if (error.code === 403)
    return dataGroupId
      ? "You don't have access to this data group's JIT settings."
      : "You don't have access to this account's JIT settings.";
  return error.description || 'Could not load the saved settings. Try again.';
};

export const JIT_NOT_SAVED_MESSAGE = 'No changes were saved. Try again.';

// Shown in the form when a save fails; error toasts are switched off app-wide.
// A refused change says who may make it, and for a data group how to recover
// when they're unavailable: whoever owns the group in ABAC manages it here.
export const getJITSaveErrorMessage = (
  error: QueryError,
  dataGroupId?: string,
) => {
  if (error.code === 403)
    return dataGroupId
      ? "Only this data group's creator or owner, an account-wide approver, the person who enabled JIT, or an admin can change this. If they're unavailable, anyone who can edit the data group can make a new owner."
      : 'Only an account-wide approver, the person who enabled JIT, or an admin can change these settings.';
  return error.description || JIT_NOT_SAVED_MESSAGE;
};

export const jitDurationOptions = [
  { label: '1 hour', value: 1 * 60 * 60 },
  { label: '2 hours', value: 2 * 60 * 60 },
  { label: '6 hours', value: 6 * 60 * 60 },
  { label: '12 hours', value: 12 * 60 * 60 },
  { label: '24 hours', value: 24 * 60 * 60 },
];

export const jitApprovalTypeOptions = [
  { label: 'Automatic', value: 'Automatic' },
  /*{ label: 'Manual', value: 'Manual' },*/
];

export const jitApprovalPolicyConditionOptions = [
  {
    label: 'Cloud',
    value: 'cloud_types',
  },
  { label: 'Account', value: 'account_ids' },
  { label: 'Data Group', value: 'data_group_ids' },
];

export const jitProviderOptions = [
  {
    label: 'AWS',
    value: CloudProvider.Aws,
    icon: getIcon(CloudProvider.Aws),
  },
  {
    label: 'GCP',
    value: CloudProvider.Gcp,
    icon: getIcon(CloudProvider.Gcp),
  },
  {
    label: 'Snowflake',
    value: CloudProvider.Snowflake,
    icon: getIcon(CloudProvider.Snowflake),
  },
  {
    label: 'Azure',
    value: CloudProvider.Azure,
    icon: getIcon(CloudProvider.Azure),
  },
];
